import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import {
  LARGE_HOLDINGS_SEARCH_AVAILABLE,
  LARGE_HOLDINGS_SORT_AVAILABLE,
} from '@/features/brokers/api/brokerListControls'
import { useLargeHoldings } from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { resolveViewState } from '@/lib/view-state'
import type { BrokerPeriod } from '@/schemas/broker'
import { pageSizeSchema } from '@/schemas/table'
import {
  LARGE_HOLDINGS_SORTS,
  largeHoldingsSearchSchema,
  toLargeHoldingsQuery,
} from '@/screens/brokers/search'
import BrokerPeriodPicker from '@/screens/brokers/components/broker/BrokerPeriodPicker'
import WindowNote from '@/screens/brokers/components/broker/WindowNote'
import { asLoadState } from '@/screens/brokers/components/broker/broker-format'
import HoldingsTable from '@/screens/brokers/components/large-holdings/HoldingsTable'
import { loadLargeHoldings } from '@/screens/brokers/LargeHoldingsScreen.loader'

const HEADER = <TitleRow title="Large Holdings" />

/** `largeHoldingsQuerySchema`'s own `pageSize` default (10, the mock's) as the row count; the
 *  table's rows are `rowHeight="lg"` (56), the broker's mark over its name. */
const TABLE_ROWS = 10

function LargeHoldingsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading large holdings…"
      header={HEADER}
      pageTitle="Large Holdings"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Skeleton className="h-11 w-full rounded-md @sm:w-70 lg:h-9" />
          <Skeleton className="h-11 w-72 rounded-md lg:h-9" />
        </div>
        <div>
          <DataTableSkeleton rows={TABLE_ROWS} columns={5} rowHeight="lg" />
          {/* `Pagination`'s own row, 24 under the table. */}
          <div className="mt-6 flex h-8 items-center justify-between gap-3">
            <Skeleton className="h-4 w-32 rounded-xs" />
            <Skeleton className="h-8 w-56 rounded-sm" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

function LargeHoldingsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Large holdings did not load" />
}

export const Route = createFileRoute('/_app/brokers/large-holdings')({
  validateSearch: largeHoldingsSearchSchema,
  loader: loadLargeHoldings,
  pendingComponent: LargeHoldingsPending,
  errorComponent: LargeHoldingsError,
  component: LargeHoldingsScreen,
})

/**
 * Brokers · Large holdings (V2 `4243:103846`).
 *
 * BUILT LATE, AND FOR NO GOOD REASON. `routes/brokers.ts` carried a comment
 * saying this flow had no backend endpoint and was pending a decision. It was
 * wrong: `GET /api/v5/brokers/large-holdings/` has been live in mm-be
 * (`services/broker/largeholdings.py`, routed in `api/v5/brokers/urls.py`) and
 * present in this app's own generated `src/api/schema.d.ts` all along. The
 * comment is corrected there rather than deleted, so the next reader can see
 * which of that trio really is blocked (Last minute movers, which has no
 * endpoint at all).
 *
 * Broker Analysis mock parity (2026-10-28): the body opens on the search and the period row (the
 * kit's inset segments and 📅), then ONE table (Stock · Broker · "Net holding, {period}" · Average
 * buy price · Share of paid-up, every head sortable, the whole row a link to the broker's page)
 * and the pager. The page footnote and the mock-less share bars are gone; the "As of" line stays.
 *
 * Search and sort are drawn but cannot run: the endpoint takes `period`, `page` and `page_size`
 * and nothing else (`brokerListControls.ts`). Typing, or sorting by anything but the served rank
 * (share of paid-up, high to low), leaves the head and the field in place over "Not available
 * yet" in the table body; clearing the field or the sort brings the page back.
 *
 * Deliberately NO sector control, though the endpoint accepts `sector`: the
 * frame draws none, and a filter the URL can hold but the screen cannot show is
 * how `sectorFlowSearchSchema` came to print "today" over three months of data.
 * See `largeHoldingsSearchSchema` for the one-line change that adds it back
 * with the control.
 */
function LargeHoldingsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const [stockOrBroker, setStockOrBroker] = useState('')

  const query = useLargeHoldings(toLargeHoldingsQuery(search))
  const data = query.data
  // `data === null` is `nullOnNoFloorsheetData`'s answer for
  // `NO_FLOORSHEET_DATA` (queries.ts) — the nightly summary has not run for
  // this window yet, not a failure. `asLoadState` folds that into `'loaded'`;
  // `notSettledYet` below is what tells `HoldingsTable` to draw "No
  // summarised sessions yet" instead of the ordinary empty table.
  const notSettledYet = data === null
  // A failed background refetch keeps the page of holdings already on
  // screen; only a data-less query reads as failed/offline (CRS-ST-2).
  const view = asLoadState(resolveViewState(query, { isEmpty: (d) => d === null, online }))

  /**
   * `keepPreviousData` holds `isLoading` FALSE across a period change, so the
   * table would otherwise keep the previous window's rows on screen, unmarked,
   * for the whole fetch. `isPlaceholderData` is what separates "the period you
   * asked for" from "the period you left" — the same pair `ByStockScreen` and
   * `LargeTradesScreen` use, and the reason neither blanks its table.
   */
  const isUpdating = query.isPlaceholderData

  /**
   * The window the rows on screen were ranked over — the RESPONSE's, so the "Net holding,
   * {period}" header never names a period the figures are not for. During that same placeholder
   * stretch `search.period` is already the new request while the rows are still the old window's;
   * labelling from the URL would print "3 months" over a week's net. The URL's period is only the
   * answer when no response is on screen to disagree with it: still loading, or `null` (not
   * settled — no rows, so the label sits over an empty-state).
   */
  const shownPeriod = data?.window.period ?? search.period

  const onPeriodChange = (next: BrokerPeriod) => {
    // Page 1: a page number from a one-week ranking means nothing against a
    // three-month one.
    void navigate({ search: (prev) => ({ ...prev, period: next, page: 1 }) })
  }

  /** The head the table is sorted by; none is the served rank (share of paid-up, high to low). */
  const sortBy = search.sort
  const sortDesc = search.dir === 'desc'
  const onSortChange = (next: string | undefined, desc: boolean) => {
    const sort = LARGE_HOLDINGS_SORTS.find((candidate) => candidate === next)
    void navigate({
      search: (prev) => ({
        ...prev,
        sort,
        dir: sort === undefined ? undefined : desc ? 'desc' : 'asc',
        page: 1,
      }),
    })
  }

  // What the table cannot do yet (brokerListControls.ts): the served rank is share of paid-up,
  // high to low, with no search. Anything else is a pick the endpoint has no param for.
  const servedRank = sortBy === undefined || (sortBy === 'pctOfCapital' && sortDesc)
  const unavailable =
    stockOrBroker.trim() !== '' && !LARGE_HOLDINGS_SEARCH_AVAILABLE
      ? 'Searching every holding by stock or broker'
      : !servedRank && !LARGE_HOLDINGS_SORT_AVAILABLE
        ? 'Sorting large holdings by this column'
        : null

  return (
    <ScreenLayout header={HEADER} pageTitle="Large Holdings">
      {/* Toolbar and table sit 24 apart (the mock); the pager keeps its own 24 under the table. */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* 280 (w-70), the mock's search: no leading glass, a ✕ while it holds text, Esc clears. */}
            <SearchInput
              aria-label="Search a stock or broker"
              placeholder="Search a stock or broker"
              leadingIcon={false}
              value={stockOrBroker}
              onChange={(event) => {
                setStockOrBroker(event.target.value)
              }}
              onClear={() => {
                setStockOrBroker('')
              }}
              className="w-full @sm:w-70"
            />
            {/* The note leads the cluster the row's `justify-between` holds against the right
                edge, so it grows leftward into free space and the segments do not slide out from
                under the pointer. */}
            <div className="flex flex-wrap items-center gap-3">
              <UpdatingNote when={isUpdating} label="Updating this period…" />
              <BrokerPeriodPicker value={search.period} onChange={onPeriodChange} />
            </div>
          </div>
          {/* `source` is hard-coded `precomputed` by `largeholdings.py`, so the partial-window
              warning can never fire here; the as-of line stays, in BS (the mock draws none). */}
          <WindowNote window={data?.window} />
        </div>

        {/* No `gap-*`: this box holds the table and its pager only, and `Pagination` draws its
            own 24px (see its PLACEMENT note). A gap here would add to that margin. */}
        <RefreshRegion updating={isUpdating} className="flex flex-col">
          <HoldingsTable
            rows={data?.holdings ?? []}
            period={shownPeriod}
            view={view}
            notSettledYet={notSettledYet}
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
            savedAt={query.dataUpdatedAt}
            pageSize={search.pageSize}
            sortBy={sortBy}
            sortDesc={sortDesc}
            onSortChange={onSortChange}
            unavailable={unavailable}
          />

          {/* Paging comes off the PAYLOAD, not the URL: this endpoint returns its own
              `page`/`pageSize`/`total` inside `data` (no list envelope), and reading the pager off
              the response is what stops the pill marked 3 from disagreeing with the rows on
              screen. Same as TradesCard. Not drawn over the not-available state: it pages rows
              that are not on screen. */}
          {data === undefined || data === null || unavailable !== null ? null : (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              unit="positions"
              onPageChange={(page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              }}
              onPageSizeChange={(pageSize) => {
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    pageSize: pageSizeSchema.parse(pageSize),
                    page: 1,
                  }),
                })
              }}
            />
          )}
        </RefreshRegion>
      </div>
    </ScreenLayout>
  )
}
